import * as Sentry from "@sentry/react";

/**
 * Initialise browser-side Sentry error reporting.
 *
 * Mirrors the worker's "no DSN => no-op" convention: `VITE_SENTRY_DSN` is a
 * build-time var set only for deployed builds (see release.yml). Local dev,
 * Capacitor, and tests leave it unset, so this returns early and the SDK never
 * loads — behaviour is unchanged there.
 *
 * The DSN is publishable (safe to embed in client JS), unlike the worker's
 * `SENTRY_DSN` wrangler secret. Events land in the same Sentry project the Vite
 * plugin already uploads client source maps to, so traces are de-minified.
 *
 * Scope is deliberately errors-only: no performance tracing, no session replay,
 * and — importantly — no console capture. That keeps expected browser console
 * noise (the Safari `interactive-widget` viewport warning and the
 * "network connection was lost" WebSocket drop on sleep/offline) out of Sentry,
 * since neither is a thrown exception.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_APP_ENV,
    // Errors only for now.
    tracesSampleRate: 0,
    // Defensive: drop the transient-network error family if it ever surfaces as
    // a real thrown rejection (fetch aborts on offline, WS teardown, etc.).
    ignoreErrors: [
      /network connection was lost/i,
      /Load failed/i,
      "Failed to fetch",
    ],
    // Single place to drop expected client errors, mirroring the worker's
    // beforeSend filter. Minimal for now — extend as expected errors surface.
    beforeSend(event) {
      return event;
    },
  });
}
